/*
 * Copyright (c) CIRROM 2026.
 */

import { API } from "./api";

/*
 * Email verification through Allegrow, the deliverability partner.
 *
 * One verdict per address, not per contact: the server caches it for the
 * revalidation window (14 days by default) and hands the stored row back with
 * `reused: true` at no cost. A fresh check spends an Allegrow credit, which is
 * why nothing calls this without a steward asking for it.
 */

export type EmailVerificationStatus =
  | "SAFE"
  | "SOME_RISK"
  | "BLOCK_BOUNCE_RISK"
  | "DEAD_EMAIL"
  | "DO_NOT_MAIL_ABUSE"
  | "UNKNOWN"
  | "ERROR"
  | "PENDING";

export interface EmailVerification {
  id: number;
  email: string;
  domain: string;
  status: EmailVerificationStatus;
  /** Allegrow's own string, for the statuses the server does not map. */
  provider_status: string | null;
  sub_status: string | null;
  mx_provider: string | null;
  is_catch_all: boolean;
  is_role_account: boolean;
  last_verified_at: string | null;
  crm_contact_id: string | null;
  data_loader_system_id: number | null;
  requested_by: string | null;
  last_error: string | null;
  /** The stored verdict came back; no credit was spent. */
  reused: boolean;
}

/** A non-200 answer, with the status the UI needs to tell the cases apart. */
export type EmailVerificationHttpError = Error & { status: number };

export const isEmailVerificationHttpError = (
  error: unknown
): error is EmailVerificationHttpError =>
  error instanceof Error && typeof (error as any).status === "number";

const detailOf = (data: any): string => {
  if (!data) return "";
  if (typeof data === "string") return data;
  if (typeof data.detail === "string") return data.detail;
  return "";
};

export const verifyEmailAddress = async (params: {
  email: string;
  /** Skip the cached verdict and ask Allegrow again. Spends a credit. */
  force?: boolean;
  crmContactId?: string | null;
  integratedSystemId?: number | null;
}): Promise<EmailVerification> => {
  /*
   * Every status resolves here rather than rejecting. The shared interceptor
   * wraps a rejection as `new Error(response.data)`, which drops the status
   * code - and 402 / 503 ("not available on this install") against 502
   * ("try again") is exactly the distinction the control has to draw.
   */
  const response = await API.post(
    "/email-verifications",
    {
      email: params.email,
      force: Boolean(params.force),
      crm_contact_id: params.crmContactId ?? undefined,
      data_loader_system_id: params.integratedSystemId ?? undefined,
    },
    { validateStatus: () => true }
  );
  if (response.status !== 200) {
    const error = new Error(
      detailOf(response.data) ||
        `Email verification failed (${response.status})`
    ) as EmailVerificationHttpError;
    error.status = response.status;
    throw error;
  }
  return response.data;
};

/** One address Allegrow refused for a contact; a contact can have several. */
export interface NegativeEmailVerificationContact {
  record_id: number;
  crm_contact_id: string | null;
  first_name: string | null;
  last_name: string | null;
  crm_account_id: string | null;
  /** The linked account's company, else the contact's own text company. */
  company: string | null;
  verification: Omit<EmailVerification, "reused">;
}

export const getNegativeEmailVerificationContacts = async (
  integratedSystemId: number
): Promise<NegativeEmailVerificationContact[]> => {
  const response = await API.get("/get-negative-email-verification-contacts", {
    params: { integrated_system_id: integratedSystemId },
  });
  return response.data;
};
